import { useState } from 'react';
import { toast } from 'react-toastify';

import {
  SitrepSectionType,
  useRemoveSitrepSectionV2,
  useUpdateSitrepSectionV2,
  useMoveSitrepSectionV2,
  useReorderSitrepSectionsV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import Sentry from '../../../instrument';

/**
 * Encapsulates section CRUD operations (save settings, delete, move) and the
 * edit-settings-drawer state for a sitrep card.
 */
const useSitrepSectionActions = (
  sitrepId: UUID,
  sections: SitrepSectionType[] | undefined,
  refetch: () => void
) => {
  const [editSettingsSection, setEditSettingsSection] =
    useState<SitrepSectionType | null>(null);

  const removeSectionMutation = useRemoveSitrepSectionV2();
  const updateSectionMutation = useUpdateSitrepSectionV2();
  const moveSectionMutation = useMoveSitrepSectionV2();
  const reorderSectionsMutation = useReorderSitrepSectionsV2();

  const handleSaveSettings = async (formData: {
    id: string;
    name: string;
    type: string;
    data_sources: string[];
    ai_instructions: string;
    output_format_structured: { summary_options: string[] };
  }) => {
    if (!editSettingsSection) return;

    try {
      await updateSectionMutation.mutateAsync({
        sitrepId,
        sectionId: editSettingsSection.id,
        updatedFields: {
          name: formData.name,
          type: formData.type,
          data_sources: formData.data_sources,
          ai_instructions: formData.ai_instructions,
          output_format_structured: formData.output_format_structured,
        },
      });
      setEditSettingsSection(null);
      await refetch();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleDeleteSection = async (section: SitrepSectionType) => {
    try {
      await removeSectionMutation.mutateAsync({
        sitrepId,
        sectionId: section.id,
      });
      await refetch();
      toast.success('Section deleted');
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to delete section');
    }
  };

  const handleMoveSection = async (direction: 'up' | 'down') => {
    if (!editSettingsSection) return;
    try {
      await moveSectionMutation.mutateAsync({
        sitrepId,
        sectionId: editSettingsSection.id,
        direction,
      });
      await refetch();
      toast.success(`Section moved ${direction}`);
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to move section');
    }
  };

  /**
   * Persists a full re-ordering of every section in the sitrep. Used by the
   * document view's category drag-and-drop, which moves an entire group's
   * sections as a contiguous block while keeping their relative order.
   */
  const handleReorderSections = async (orderedSectionIds: string[]) => {
    try {
      await reorderSectionsMutation.mutateAsync({
        sitrepId,
        orderedSectionIds,
      });
      await refetch();
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to reorder sections');
    }
  };

  // Calculate section position for move button states. Moving swaps with the
  // adjacent section in the flat order, so a move across a category boundary
  // would split that category's contiguous run into a duplicate group —
  // restrict moves to neighbors within the same category.
  const sectionList = sections ?? [];
  const sectionIndex = sectionList.findIndex(
    (s) => s.id === editSettingsSection?.id
  );
  const sectionCategory = editSettingsSection?.category ?? null;
  const canMoveUp =
    sectionIndex > 0 &&
    (sectionList[sectionIndex - 1].category ?? null) === sectionCategory;
  const canMoveDown =
    sectionIndex >= 0 &&
    sectionIndex < sectionList.length - 1 &&
    (sectionList[sectionIndex + 1].category ?? null) === sectionCategory;

  return {
    editSettingsSection,
    setEditSettingsSection,
    handleSaveSettings,
    handleDeleteSection,
    isDeletingSection: removeSectionMutation.isPending,
    handleMoveSection,
    canMoveUp,
    canMoveDown,
    handleReorderSections,
  } as const;
};

export default useSitrepSectionActions;
